import { Button } from "@/components/ui/button";
import { cn } from "@/utils";
import React from "react";
import { SortHeaderButton } from "../../components/SortableHeader";
import {
  cardGridTrackStyle,
  workspaceCanvasCardGridClassName,
} from "../../workspaceGridClasses";
import {
  browserItemId,
  type BrowserItem,
  type HomeSort,
  type HomeSortField,
  type HomeViewMode,
} from "../utils/homeBrowserTypes";
import type { HomeEmptyCopy } from "../utils/homeEmptyCopy";
import { BROWSER_LIST_COL } from "./browserListColumns";

interface HomeBrowserListProps {
  view: HomeViewMode;
  items: BrowserItem[];
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
  empty: HomeEmptyCopy;
  renderGridItem: (item: BrowserItem) => React.ReactNode;
  renderRow: (item: BrowserItem) => React.ReactNode;
  /**
   * Element rendered raw before the items — the first grid cell in grid view
   * or the first row in list view (the caller supplies the matching form per
   * view; e.g. the finish-onboarding card/row in Recents). Its presence also
   * suppresses the empty state so it can show on its own when there are no
   * items yet.
   */
  leadingSlot?: React.ReactNode;
  /**
   * Optional grid card track width in px (via the `--ws-card-track` knob), used
   * by the home browser to run a denser grid than the default track.
   */
  gridTrackPx?: number;
  /** Column-header-driven sort. When both are set the list's column labels
   *  become sort toggles (active field flips direction, a new field jumps to
   *  its natural default); without them the header is static text. */
  sort?: HomeSort;
  onSortFieldChange?: (field: HomeSortField) => void;
  /**
   * Folder cards rendered ahead of the items in grid view (workspace browser
   * drill-in). Counted into the emptiness check so a folders-only level
   * doesn't show the empty state.
   */
  folderCards?: React.ReactNode[];
  /**
   * Replaces the default `items.map(renderRow)` list body — the workspace
   * browser's inline folder tree — while the column header and the
   * pending/error/empty chrome stay shared. Grid view ignores it. Its
   * presence counts as content for the empty-state check (a folders-only
   * level has no loose `items`), so pass it only when the tree has rows.
   */
  listBody?: React.ReactNode;
}

/**
 * The floating grid/list body — no enclosing tile. The surface above (greeting,
 * composer, tab toolbar — and here, list view's column header) stays fixed;
 * this component fills the surface's remaining height and only the item body
 * scrolls. The root is an `@container` so the row/column responsive shedding
 * measures the list's own width.
 */
export function HomeBrowserList({
  view,
  items,
  isPending,
  isError,
  onRetry,
  empty,
  renderGridItem,
  renderRow,
  leadingSlot,
  gridTrackPx,
  sort,
  onSortFieldChange,
  folderCards,
  listBody,
}: HomeBrowserListProps) {
  const gridStyle =
    gridTrackPx != null ? cardGridTrackStyle(gridTrackPx) : undefined;
  const folderCount = folderCards?.length ?? 0;

  const sortable = !!sort && !!onSortFieldChange;
  /** Sort toggle when wired, static label otherwise (e.g. trash-like hosts). */
  const headerLabel = (field: HomeSortField, label: string) =>
    sortable ? (
      <SortHeaderButton
        field={field}
        label={label}
        activeField={sort.field}
        sortDir={sort.dir}
        onSortChange={onSortFieldChange}
      />
    ) : (
      label
    );

  return (
    <div className="@container flex min-h-0 min-w-0 flex-1 flex-col">
      {view === "list" && (
        // No negative margin here (unlike the rows): the border-b must start
        // at the column edge, not hang into the page padding. The trailing
        // columns still line up because both right edges match; only the
        // Name label needs its own offset.
        <div className="flex shrink-0 items-center gap-3 border-b pb-1.5 pr-2.5 pt-0.5 text-xs font-medium text-muted-foreground">
          {/* pl-3.5 puts "Name" flush with the preview tiles' left edge
              (the rows hang 6px left, then gutter 14px + halved gap 6px). */}
          <span className="min-w-0 flex-1 truncate pl-3.5">
            {headerLabel("name", "Name")}
          </span>
          {/* Static label — Location isn't in the sort model (its display
              value needs the workspace list, which the pure item
              comparators don't see). */}
          <span className={BROWSER_LIST_COL.location}>Location</span>
          {/* Static labels — single-kind tabs left Type/Size with nothing
              meaningful to sort; the columns remain display-only. */}
          <span className={BROWSER_LIST_COL.type}>Type</span>
          <span className={BROWSER_LIST_COL.size}>Size</span>
          <span className={BROWSER_LIST_COL.modified}>
            {headerLabel("modified", "Last updated")}
          </span>
        </div>
      )}

      {/* pb-16 replaces the page-level bottom padding that scrolled away with
          the old whole-page scroll — content still ends with breathing room.
          -mx/px pair: the scroll container clips overflow, and the list rows
          hang 6px left (chevron gutter) — widen the container by 6px per side
          and pad it back so content stays aligned while the overhang renders. */}
      <div className="-mx-1.5 min-h-0 flex-1 overflow-y-auto px-1.5 pb-16 pt-2">
        {isPending ? (
          view === "grid" ? (
            <div
              className={cn(workspaceCanvasCardGridClassName, "px-1 pb-2")}
              style={gridStyle}
            >
              {Array.from({ length: 6 }, (_, i) => (
                <div
                  key={i}
                  className="aspect-[16/10] animate-pulse rounded-lg bg-muted/50"
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-0.5 pb-2">
              {Array.from({ length: 8 }, (_, i) => (
                <div
                  key={i}
                  className="mx-2.5 h-12 animate-pulse rounded-md bg-muted/40"
                />
              ))}
            </div>
          )
        ) : isError && items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 p-8 text-center">
            <p className="text-sm text-muted-foreground">
              Couldn't load this view.
            </p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Try again
            </Button>
          </div>
        ) : items.length === 0 &&
          folderCount === 0 &&
          !listBody &&
          !leadingSlot ? (
          <div className="flex flex-col items-center justify-center gap-1 p-8 text-center">
            <p className="text-sm text-muted-foreground">{empty.title}</p>
            {empty.hint && (
              <p className="text-xs text-muted-foreground/70">{empty.hint}</p>
            )}
          </div>
        ) : view === "grid" ? (
          <div
            className={cn(workspaceCanvasCardGridClassName, "px-1 pb-2")}
            style={gridStyle}
          >
            {leadingSlot}
            {folderCards}
            {items.map((item) => (
              <React.Fragment key={browserItemId(item)}>
                {renderGridItem(item)}
              </React.Fragment>
            ))}
          </div>
        ) : (
          /* Zebra stripes, via custom-property indirection: every even child
             (one visible row each — the workspace trees flatten to sibling
             fragments) gets `--row-stripe`, which inherits through whatever
             wrapper (SelectableItem, drop target, depth padding) to the row,
             whose base background reads it. Styling the rows from here
             directly would out-specificity their own hover/selected
             backgrounds; the variable carries no specificity at all. */
          <div className="flex flex-col gap-0.5 pb-2 [&>:nth-child(even)]:[--row-stripe:color-mix(in_oklab,var(--n-10)_30%,transparent)]">
            {leadingSlot}
            {listBody ?? items.map((item) => renderRow(item))}
          </div>
        )}
      </div>
    </div>
  );
}
